/**
 * Command palette (Cmd+K) for semantic search across all provenance nodes.
 *
 * Uses the `cmdk` library for the command palette UI pattern, backed by
 * the pgvector semantic search API endpoint. Engineers type plain English
 * and Thread navigates them to the matching run, dataset, plot, config,
 * branch, or comment.
 */

import { Command } from "cmdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { semanticSearch, type NodeType, type SearchResult } from "../../api/search";
import { useProvenanceContext } from "../../context/ProvenanceContext";

import "./command-palette.css";

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------

const RECENT_SEARCHES_KEY = "thread:recent-searches";
const MAX_RECENT = 5;

const EXAMPLE_QUERIES = [
  "high alpha runs that didn't converge",
  "drag anomalies",
  "validation cases",
  "grid convergence study",
  "runs near stall",
  "sideslip conditions",
];

const NODE_TYPE_ICONS: Record<string, string> = {
  run: "\u25B6",             // ▶
  geometry: "\u25C7",        // ◇
  surface_mesh: "\u25A4",    // ▤
  volume_mesh: "\u25A5",     // ▥
  reference_quantity: "\u0394",  // Δ
  scalar: "#",
  dataset: "\u25A6",         // ▦
  dataframe: "\u25A6",       // ▦
  plot: "\u25C8",            // ◈
  saved_plot: "\u25C8",      // ◈
  file_ref: "\u25A3",        // ▣
  config: "\u2699",          // ⚙
  comment: "\u25AC",         // ▬
  branch: "\u2387",          // ⎇
  metadata: "\u2139",        // ℹ
};

const NODE_TYPE_LABELS: Record<string, string> = {
  run: "Run",
  geometry: "Geometry",
  surface_mesh: "Surface Mesh",
  volume_mesh: "Volume Mesh",
  reference_quantity: "Reference Quantity",
  scalar: "Scalar",
  dataset: "Dataset",
  dataframe: "Dataframe",
  plot: "Plot",
  saved_plot: "Saved Plot",
  file_ref: "File",
  config: "Configuration",
  comment: "Comment",
  branch: "Branch",
  metadata: "Metadata",
};

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

function loadRecentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveRecentSearch(query: string) {
  const recent = loadRecentSearches().filter((q) => q !== query);
  recent.unshift(query);
  localStorage.setItem(
    RECENT_SEARCHES_KEY,
    JSON.stringify(recent.slice(0, MAX_RECENT)),
  );
}

/** Group search results by node_type. */
function groupByType(results: SearchResult[]): Map<NodeType, SearchResult[]> {
  const groups = new Map<NodeType, SearchResult[]>();
  for (const r of results) {
    const t = r.node_type;
    if (!groups.has(t)) groups.set(t, []);
    groups.get(t)!.push(r);
  }
  return groups;
}

// -----------------------------------------------------------------------
// Command palette component
// -----------------------------------------------------------------------

export function CommandPalette() {
  const { projectId, currentBranch, selectArtifact, setOpenPanel } =
    useProvenanceContext();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  // Debounced search query (300ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // API query
  const { data, isLoading } = useQuery({
    queryKey: ["semantic-search", projectId, currentBranch, debouncedQuery],
    queryFn: () => semanticSearch(projectId, debouncedQuery, currentBranch),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  });

  const handleSelect = useCallback(
    (result: SearchResult) => {
      saveRecentSearch(debouncedQuery || query);
      if (result.node_id) {
        selectArtifact(result.node_id);
        // Navigate to appropriate panel based on node type
        const panelMap: Record<string, string> = {
          run: "data",
          geometry: "dag",
          surface_mesh: "dag",
          volume_mesh: "dag",
          reference_quantity: "config",
          scalar: "data",
          dataset: "data",
          dataframe: "data",
          plot: "saved-plots",
          saved_plot: "saved-plots",
          file_ref: "data",
          config: "config",
          comment: "conversations",
          branch: "dag",
          metadata: "data",
        };
        setOpenPanel((panelMap[result.node_type] || "data") as any);
      }
      setOpen(false);
    },
    [selectArtifact, setOpenPanel, debouncedQuery, query],
  );

  const handleExampleClick = useCallback((q: string) => {
    setQuery(q);
    setDebouncedQuery(q);
  }, []);

  const recentSearches = loadRecentSearches();
  const grouped = data?.results ? groupByType(data.results) : null;
  const hasResults = data && data.results.length > 0;
  const showEmpty = data && data.results.length === 0 && debouncedQuery.length >= 2;
  const showRecent = !debouncedQuery && recentSearches.length > 0;
  const showExamples = !debouncedQuery && !showRecent;

  return (
    <>
      {/* Trigger button */}
      <button
        className="cmdk-trigger"
        onClick={() => setOpen(true)}
        title="Search (Cmd+K)"
      >
        <SearchIcon />
        <span className="cmdk-trigger-label">Search...</span>
        <kbd className="cmdk-trigger-kbd">
          {navigator.platform?.includes("Mac") ? "\u2318" : "Ctrl+"}K
        </kbd>
      </button>

      {/* Command palette dialog */}
      <Command.Dialog
        open={open}
        onOpenChange={setOpen}
        label="Search provenance"
        className="cmdk-dialog"
        shouldFilter={false}
      >
        {/* Input */}
        <div className="cmdk-input-wrapper">
          <SearchIcon />
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder="Search simulations in plain English..."
            className="cmdk-input"
          />
          {isLoading && <div className="cmdk-spinner" />}
          <kbd className="cmdk-kbd">Esc</kbd>
        </div>

        <Command.List className="cmdk-list">
          {/* Recent searches */}
          {showRecent && (
            <Command.Group heading="Recent" className="cmdk-group">
              {recentSearches.map((q) => (
                <Command.Item
                  key={`recent-${q}`}
                  className="cmdk-item"
                  onSelect={() => {
                    setQuery(q);
                    setDebouncedQuery(q);
                  }}
                >
                  <ClockIcon />
                  <span>{q}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* Example queries */}
          {showExamples && (
            <Command.Group heading="Try an example" className="cmdk-group">
              {EXAMPLE_QUERIES.map((q) => (
                <Command.Item
                  key={`ex-${q}`}
                  className="cmdk-item"
                  onSelect={() => handleExampleClick(q)}
                >
                  <SparkleIcon />
                  <span>{q}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {/* Grouped results */}
          {hasResults &&
            grouped &&
            Array.from(grouped.entries()).map(([type, results]) => (
              <Command.Group
                key={type}
                heading={(NODE_TYPE_LABELS[type] || type) + "s"}
                className="cmdk-group"
              >
                {results.map((result) => (
                  <Command.Item
                    key={result.node_id}
                    className="cmdk-item"
                    value={result.node_id}
                    onSelect={() => handleSelect(result)}
                  >
                    <ResultRow result={result} maxScore={data!.results[0]?.score ?? 1} query={debouncedQuery} />
                  </Command.Item>
                ))}
              </Command.Group>
            ))}

          {/* Empty state */}
          {showEmpty && (
            <Command.Empty className="cmdk-empty">
              No matching items in this project. Try refining your query.
            </Command.Empty>
          )}
        </Command.List>

        {/* Footer */}
        <div className="cmdk-footer">
          <span>
            <kbd className="cmdk-kbd">&uarr;&darr;</kbd> navigate
          </span>
          <span>
            <kbd className="cmdk-kbd">&crarr;</kbd> select
          </span>
          <span>
            <kbd className="cmdk-kbd">esc</kbd> close
          </span>
          {data && data.count > 0 && (
            <span className="cmdk-footer-count">
              {data.count} result{data.count !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </Command.Dialog>
    </>
  );
}

// -----------------------------------------------------------------------
// Result row
// -----------------------------------------------------------------------

function ResultRow({
  result,
  maxScore,
  query,
}: {
  result: SearchResult;
  maxScore: number;
  query: string;
}) {
  const icon = NODE_TYPE_ICONS[result.node_type] || "\u25CF";
  const scoreRatio = maxScore > 0 ? result.score / maxScore : 0;
  const scoreColor =
    scoreRatio > 0.8
      ? "var(--green-9, #22c55e)"
      : scoreRatio > 0.6
        ? "var(--blue-9, #3b82f6)"
        : scoreRatio > 0.4
          ? "var(--yellow-9, #eab308)"
          : "var(--gray-9, #6b7280)";

  const title =
    result.name ||
    result.semantic_text?.slice(0, 80) ||
    result.node_id.slice(0, 8);

  // Use match_reason as the explanation subtitle
  const reason = result.match_reason || result.semantic_text?.slice(0, 120) || "";

  // Run metadata lines (separate from the reason)
  let runMeta = "";
  let runExtra = "";
  if (result.node_type === "run") {
    const parts: string[] = [];
    if (result.geom_name) parts.push(result.geom_name);
    if (result.alpha != null) parts.push(`\u03b1=${result.alpha}\u00b0`);
    if (result.mach != null) parts.push(`M=${result.mach}`);
    if (result.CL != null) parts.push(`CL=${Number(result.CL).toFixed(4)}`);
    if (result.CD != null) parts.push(`CD=${Number(result.CD).toFixed(5)}`);
    if (result.L_D != null) parts.push(`L/D=${Number(result.L_D).toFixed(1)}`);
    runMeta = parts.join("  ");

    // Second line: mesh, convergence, solver
    const extra: string[] = [];
    if (result.mesh) extra.push(result.mesh);
    if (result.mesh_cell_count != null) {
      const mc = result.mesh_cell_count;
      extra.push(mc >= 1e6 ? `${(mc / 1e6).toFixed(1)}M cells` : `${(mc / 1e3).toFixed(0)}k cells`);
    }
    if (result.residual != null) extra.push(`res=${Number(result.residual).toExponential(1)}`);
    if (result.iterations != null) extra.push(`${result.iterations} iter`);
    if (result.turbulence) extra.push(result.turbulence);
    if (result.Re != null) {
      const re = result.Re;
      extra.push(re >= 1e6 ? `Re=${(re / 1e6).toFixed(1)}M` : `Re=${(re / 1e3).toFixed(0)}k`);
    }
    runExtra = extra.join("  ");
  }

  return (
    <div className="cmdk-result-row">
      <span className="cmdk-result-icon">{icon}</span>
      <div className="cmdk-result-content">
        <div className="cmdk-result-title">
          {title}
          {result.status && <StatusBadge status={result.status} />}
        </div>
        {reason && (
          <div className="cmdk-result-reason">
            <HighlightedText text={reason} query={query} />
          </div>
        )}
        {runMeta && (
          <div className="cmdk-result-subtitle">{runMeta}</div>
        )}
        {runExtra && (
          <div className="cmdk-result-subtitle cmdk-result-extra">{runExtra}</div>
        )}
      </div>
      <div className="cmdk-result-score">
        <div className="cmdk-score-bar">
          <div
            className="cmdk-score-fill"
            style={{ width: `${scoreRatio * 100}%`, background: scoreColor }}
          />
        </div>
        <span className="cmdk-score-text" style={{ color: scoreColor }}>
          {result.score.toFixed(3)}
        </span>
      </div>
    </div>
  );
}

/** Highlight query terms within a text string. */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query || query.length < 2) return <>{text}</>;

  // Keep short uppercase tokens (CL, CD, CM) — they're engineering symbols
  const tokens = query.split(/\s+/).filter((t) => t.length > 2 || /^[A-Z]/.test(t));
  if (!tokens.length) return <>{text}</>;

  const pattern = new RegExp(`(${tokens.map(escapeRegex).join("|")})`, "gi");
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) =>
        pattern.test(part) ? (
          <mark key={i} className="cmdk-highlight">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// -----------------------------------------------------------------------
// Status badge
// -----------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, { bg: string; fg: string }> = {
    completed: { bg: "rgba(34,197,94,0.15)", fg: "#22c55e" },
    unconverged: { bg: "rgba(234,179,8,0.15)", fg: "#eab308" },
    running: { bg: "rgba(59,130,246,0.15)", fg: "#3b82f6" },
  };
  const colors = colorMap[status] || {
    bg: "var(--gray-a3, rgba(0,0,0,0.05))",
    fg: "var(--gray-11, #6b7280)",
  };

  return (
    <span
      className="cmdk-status-badge"
      style={{ background: colors.bg, color: colors.fg }}
    >
      {status}
    </span>
  );
}

// -----------------------------------------------------------------------
// Icons (inline SVG to avoid dependencies)
// -----------------------------------------------------------------------

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="cmdk-icon"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="cmdk-icon"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="cmdk-icon"
    >
      <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26z" />
    </svg>
  );
}
